import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const issueCount = (data) => (data.duplicate_id_count ?? 0)
  + (data.missing_from_feed_count ?? 0)
  + (data.orphan_feed_entity_count ?? 0);

/**
 * Entities feed integrity diagnostic: reproduces the exact GET /api/entities
 * query to find (1) ids that appear more than once in the feed, (2) ENTITY_DEFS
 * entries absent from the feed with the mechanism that explains why, and
 * (3) feed entities with no matching ENTITY_DEFS entry. Read-only.
 */
export function EntitiesFeedIntegrityPanel() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  // Per-duplicate-id dedup review state, keyed by entity id. Never applies
  // without a fresh preview first -- the button flow is preview -> confirm,
  // not a single click, since this deletes a Mongo document.
  const [dedup, setDedup] = useState({});

  const runScan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.entitiesFeedIntegrity();
      setResult(data);
      const issues = issueCount(data);
      if (issues === 0) {
        toast.success('No entities-feed integrity issues found');
      } else {
        toast.success(`Found ${issues} issue(s) in the entities feed`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setScanning(false);
    }
  };

  const previewDedup = async (entityId) => {
    setDedup((prev) => ({ ...prev, [entityId]: { status: 'previewing' } }));
    try {
      const { data } = await diagnosticsAPI.entitiesFeedIntegrityDedupPreview(entityId);
      setDedup((prev) => ({ ...prev, [entityId]: { status: data.status, preview: data } }));
      if (data.status === 'diverged') {
        toast.error('Twins have diverged -- refusing to auto-pick a survivor');
      }
    } catch (err) {
      setDedup((prev) => ({ ...prev, [entityId]: { status: 'error' } }));
      toast.error(getErrorMessage(err));
    }
  };

  const applyDedup = async (entityId) => {
    setDedup((prev) => ({ ...prev, [entityId]: { ...prev[entityId], status: 'applying' } }));
    try {
      const { data } = await diagnosticsAPI.entitiesFeedIntegrityDedupApply(entityId);
      setDedup((prev) => ({ ...prev, [entityId]: { status: 'applied', preview: data } }));
      toast.success(
        data.status === 'deduped'
          ? `Removed ${data.deleted_count} identical twin(s); kept ${data.survivor_id}`
          : 'Nothing left to remove'
      );
      await runScan();
    } catch (err) {
      setDedup((prev) => ({ ...prev, [entityId]: { ...prev[entityId], status: 'error' } }));
      toast.error(getErrorMessage(err));
    }
  };

  const noIssues = result && issueCount(result) === 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Entities Feed Integrity</CardTitle>
        <p className="text-xs text-muted-foreground">
          Reproduces GET /api/entities exactly, then cross-checks it against ENTITY_DEFS
          for duplicate ids, missing entities, and orphans. Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button onClick={runScan} disabled={scanning} variant="outline" size="sm">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Scan Entities Feed
          </Button>
        </div>

        {result && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>Source: <span className="font-mono">{result.feed_source}</span></div>
              <div>
                Feed count: <strong>{result.feed_count}</strong>{result.feed_count_hit_limit && (
                  <span className="text-amber-600"> (hit the {result.feed_limit}-doc cap; feed may be truncated)</span>
                )} &middot; ENTITY_DEFS count: <strong>{result.entity_defs_count}</strong>
              </div>
            </div>

            {noIssues && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" /> No duplicate ids, missing entities, or orphans detected.
              </div>
            )}

            {/* 1. Duplicate ids */}
            {result.duplicate_id_count > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  Duplicate ids in feed ({result.duplicate_id_count})
                </div>
                {result.duplicate_ids.map((dup, di) => (
                  <div key={di} className="border rounded p-3 space-y-2">
                    <div className="text-xs">
                      id <Badge variant="outline" className="font-mono">{dup.id}</Badge> appears{' '}
                      <strong>{dup.occurrences.length}</strong> times
                    </div>
                    <div className="border rounded overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">Name</th>
                            <th className="text-left px-2 py-1.5 font-medium">Type</th>
                            <th className="text-left px-2 py-1.5 font-medium">Country</th>
                            <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dup.occurrences.map((doc, oi) => (
                            <tr key={oi} className="border-b last:border-0 hover:bg-muted/20">
                              <td className="px-2 py-1.5">{doc.name}</td>
                              <td className="px-2 py-1.5 font-mono">{doc.type}</td>
                              <td className="px-2 py-1.5">{doc.country}</td>
                              <td className="px-2 py-1.5">{doc.currency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Dedup: "Remove identical twin" -- scoped to the
                        byte-identical case only, preview before apply. */}
                    {(() => {
                      const state = dedup[dup.id];
                      if (!state || state.status === 'error') {
                        return (
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => previewDedup(dup.id)}
                              variant="outline"
                              size="sm"
                              data-testid={'dedup-preview-btn-' + dup.id}
                            >
                              <Search className="h-3 w-3 mr-1.5" /> Check for identical twin
                            </Button>
                            {state?.status === 'error' && (
                              <span className="text-xs text-red-600">Check failed, try again</span>
                            )}
                          </div>
                        );
                      }
                      if (state.status === 'previewing' || state.status === 'applying') {
                        return (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {state.status === 'previewing' ? 'Comparing twins field by field...' : 'Removing...'}
                          </div>
                        );
                      }
                      if (state.status === 'diverged') {
                        return (
                          <div className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded p-2" data-testid={'dedup-diverged-' + dup.id}>
                            <div className="flex items-center gap-1.5 font-medium mb-1">
                              <AlertTriangle className="h-3.5 w-3.5" /> Refused: twins have diverged
                            </div>
                            {state.preview?.message}
                          </div>
                        );
                      }
                      if (state.status === 'byte_identical') {
                        return (
                          <div className="text-xs bg-muted/40 rounded p-2 space-y-2">
                            <div>
                              Twins are byte-identical. Survivor (oldest): <span className="font-mono">{state.preview.survivor_id}</span>
                              {' '}&middot; will remove <strong>{state.preview.delete_ids.length}</strong> duplicate(s).
                            </div>
                            <Button
                              onClick={() => applyDedup(dup.id)}
                              variant="destructive"
                              size="sm"
                              data-testid={'dedup-apply-btn-' + dup.id}
                            >
                              <Trash2 className="h-3 w-3 mr-1.5" /> Remove identical twin
                            </Button>
                          </div>
                        );
                      }
                      if (state.status === 'applied') {
                        return (
                          <div className="flex items-center gap-1.5 text-xs text-green-600" data-testid={'dedup-applied-' + dup.id}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {state.preview?.status === 'deduped'
                              ? `Removed ${state.preview.deleted_count} duplicate(s); kept ${state.preview.survivor_id}`
                              : 'Already resolved'}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ))}
              </div>
            )}

            {/* 2. Missing from feed */}
            {result.missing_from_feed_count > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  ENTITY_DEFS entries absent from feed ({result.missing_from_feed_count})
                </div>
                {result.missing_from_feed.map((m, mi) => (
                  <div key={mi} className="border rounded p-3 space-y-2">
                    <div className="text-sm">
                      <Badge variant="outline" className="text-red-600 border-red-300">
                        {m.entity_def.canonical_name}
                      </Badge>{' '}
                      <span className="text-xs text-muted-foreground font-mono">
                        {m.entity_def.uuid}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      def: {m.entity_def.entity_type} &middot; {m.entity_def.currency} &middot;{' '}
                      {m.entity_def.jurisdiction ?? '(no jurisdiction)'}
                    </div>
                    <div className="text-xs bg-muted/40 rounded p-2">{m.mechanism}</div>
                    {m.evidence_docs?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">
                          Evidence docs ({m.evidence_docs.length})
                        </summary>
                        <div className="border rounded mt-1 overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left px-2 py-1 font-medium">id</th>
                                <th className="text-left px-2 py-1 font-medium">name</th>
                                <th className="text-left px-2 py-1 font-medium">retired</th>
                              </tr>
                            </thead>
                            <tbody>
                              {m.evidence_docs.map((doc, ei) => (
                                <tr key={ei} className="border-b last:border-0">
                                  <td className="px-2 py-1 font-mono">{doc.id}</td>
                                  <td className="px-2 py-1">{doc.name}</td>
                                  <td className="px-2 py-1">{doc.retired ? 'true' : 'false'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 3. Orphan feed entities */}
            {result.orphan_feed_entity_count > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Feed entities with no ENTITY_DEFS match ({result.orphan_feed_entity_count})
                </div>
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">id</th>
                        <th className="text-left px-2 py-1.5 font-medium">Name</th>
                        <th className="text-left px-2 py-1.5 font-medium">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.orphan_feed_entities.map((doc, oi) => (
                        <tr key={oi} className="border-b last:border-0 hover:bg-muted/20">
                          <td className="px-2 py-1.5 font-mono">{doc.id}</td>
                          <td className="px-2 py-1.5">{doc.name}</td>
                          <td className="px-2 py-1.5 font-mono">{doc.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
